import { useState, useEffect, useCallback, useRef } from 'react';

interface UseIdleDetectionOptions {
  timeout?: number; // ms before considered idle
  onIdle?: () => void;
  onActive?: () => void;
  enabled?: boolean;
}

export const useIdleDetection = ({
  timeout = 2000,
  onIdle,
  onActive,
  enabled = true,
}: UseIdleDetectionOptions = {}) => {
  const [isIdle, setIsIdle] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isIdleRef = useRef(false);

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isIdleRef.current) {
      isIdleRef.current = false;
      setIsIdle(false);
      onActive?.();
    }

    timeoutRef.current = setTimeout(() => {
      isIdleRef.current = true;
      setIsIdle(true);
      onIdle?.();
    }, timeout);
  }, [timeout, onIdle, onActive, enabled]);

  useEffect(() => {
    if (!enabled) {
      setIsIdle(false);
      return;
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

    events.forEach((event) => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    // Start the timer immediately
    resetTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimer, enabled]);

  return { isIdle, resetTimer };
};

// Hook to hide cursor when idle
export const useHideCursor = (enabled: boolean = true, timeout: number = 2000) => {
  const { isIdle } = useIdleDetection({ timeout, enabled });

  useEffect(() => {
    if (!enabled) return;

    if (isIdle) {
      document.body.style.cursor = 'none';
    } else {
      document.body.style.cursor = 'auto';
    }

    return () => {
      document.body.style.cursor = 'auto';
    };
  }, [isIdle, enabled]);

  return isIdle;
};
